/**
 * طبقة الاتصال لشاشة «فريقي» (design/Team.dc.html).
 *
 * الشاشة بتقرا من أربع نقط موجودة فعلًا في السيستم:
 *   • `GET  /tasks/queue?dept=`                  — الطابور غير الموزّع
 *   • `GET  /tasks/overdue?scope=department`     — متأخرات الفريق
 *   • `GET  /admin/users?department=`            — أعضاء الفريق
 *   • `GET  /tasks?scope=department&state=…`     — الحِمل (بيتجمّع هنا)
 *   • `POST /tasks/{id}/claim` · `POST /tasks/{id}/reassign`
 *
 * ⚠️ الحِمل بيتحسب في الواجهة لأن السيستم لسه مش بيرجّع حِمل كل عضو.
 *    ⛔ وممنوع ندّعي رقم مش عارفينه: لو الصفحات خلصت والسيستم لسه عنده
 *    مهام زيادة، بنرجّع `partial: true` والشاشة بتقول للمستخدم إن الأرقام
 *    على جزء من المهام — مش بنسكت وناخد الرقم الناقص على إنه الحقيقة.
 *    و«اتقفل النهارده» و«آخر نشاط» مش موجودين في أي رد ⇒ `null` وبيتعرضوا
 *    «—» بدل صفر مخترَع.
 *
 * ⛔ ممنوع «نجاح» على رد فاضي: أي رد مش قائمة/مش كائن بيرمي خطأ صريح.
 */
import { api, ApiError, OfflineError, errorMessageAr } from "@/api/client";

/* ══════════════ أنواع ══════════════ */

export type TeamTask = {
  id: string;
  title_ar: string;
  body_ar: string;
  source: string;
  source_label_ar: string;
  state: string;
  dept: string | null;
  assignee_id: string | null;
  entity_label_ar: string;
  is_overdue: boolean;
  sla_label_ar: string | null;
  due_at: string | null;
  version: number;
};

export type QueueItem = TeamTask & {
  /** نص السيستم: «في الطابور من 25 دقيقة» */
  waiting_label_ar: string;
  /** عدّى مهلة الاستلام ⇒ هيتصعّد للمنسقة */
  will_escalate: boolean;
  /** القناة دي مالهاش مدخل آلي — الفريق بيسجّلها بإيده */
  manual_entry: boolean;
};

export type TeamMember = {
  id: string;
  name_ar: string;
  role_ar: string | null;
  /** الحساب يقدر يستلم شغل جديد (نشط) — المعلّق أو الموقوف لأ */
  can_receive: boolean;
  open_tasks: number;
  overdue_tasks: number;
  /** 🔻 مش بيرجعوا من السيستم لحد دلوقتي — بيتعرضوا «—» */
  closed_today: number | null;
  last_activity_at: string | null;
};

export type Workload = {
  members: TeamMember[];
  /** الأرقام محسوبة على جزء من المهام بس */
  partial: boolean;
  scanned: number;
};

/** نتيجة قسم من الشاشة — كل قسم بيفشل لوحده بدل ما الشاشة كلها تقع */
export type Loaded<T> =
  | { ok: true; value: T }
  | { ok: false; offline: boolean; messageAr: string };

export type TeamBoard = {
  queue: Loaded<QueueItem[]>;
  overdue: Loaded<TeamTask[]>;
  workload: Loaded<Workload>;
};

/* ══════════════ ثوابت ══════════════ */

/** أقصى عدد صفحات بنقراها عشان نحسب الحِمل — بعدها بنقول «الأرقام جزئية» */
const MAX_WORKLOAD_PAGES = 5;
const WORKLOAD_PAGE_SIZE = 100;

/**
 * مصادر المهام اللي **مالهاش مدخل آلي** — حد من الفريق بيكتبها بإيده.
 * (رسالة واتساب · طلب تعديل موعد جاي من البورتال · أي حاجة اتسندت بالإيد).
 * ⛔ ممنوع نعرضها كأن السيستم بيسحبها لوحده.
 */
const MANUAL_SOURCES = new Set(["self", "assigned", "manual"]);

/* ══════════════ أدوات ══════════════ */

function fail(path: string, reason: string): never {
  // ⛔ ممنوع الفشل الصامت — الرد الفاضي عطل مش حالة عادية
  console.error(`[team] ${reason} (${path})`);
  throw new ApiError(
    502,
    { code: "EMPTY_RESPONSE", message_ar: "السيستم رجّع رد مش مفهوم — مقدرناش نعرض البيانات" },
    null,
  );
}

function asArray(rows: unknown, path: string): Record<string, unknown>[] {
  if (!Array.isArray(rows)) fail(path, "الرد مش قائمة");
  return rows as Record<string, unknown>[];
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function strOrNull(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

function num(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function normalizeTask(raw: Record<string, unknown>, path: string): TeamTask {
  const id = str(raw["id"]);
  if (!id) fail(path, "مهمة بلا معرّف في الرد");
  const entity = (raw["entity"] ?? {}) as Record<string, unknown>;
  return {
    id,
    title_ar: str(raw["title_ar"]),
    body_ar: str(raw["body_ar"]),
    source: str(raw["source"]),
    source_label_ar: str(raw["source_label_ar"]),
    state: str(raw["state"]),
    dept: strOrNull(raw["dept"]),
    assignee_id: strOrNull(raw["assignee_id"]),
    entity_label_ar: str(entity["label_ar"]),
    is_overdue: raw["is_overdue"] === true,
    sla_label_ar: strOrNull(raw["sla_label_ar"]),
    due_at: strOrNull(raw["due_at"]),
    version: num(raw["version"], 1),
  };
}

/** مفتاح تكرار للكتابات اللي المواصفة بتفرض عليها `Idempotency-Key` */
function newIdempotencyKey(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `rtl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** بيحوّل أي عطل لقسم فاشل — الشاشة بتعرض السبب، ما بتبلعش */
function toFailure(err: unknown, where: string): { ok: false; offline: boolean; messageAr: string } {
  console.error(`[team] ${where} فشل`, err);
  return { ok: false, offline: err instanceof OfflineError, messageAr: errorMessageAr(err) };
}

/* ══════════════ القراءة ══════════════ */

export async function fetchQueue(dept: string | null): Promise<QueueItem[]> {
  const path = "/tasks/queue";
  const res = await api.get<unknown>(path, { query: { dept, per_page: 50 } });
  return asArray(res.data, path).map((raw) => {
    const task = normalizeTask(raw, path);
    return {
      ...task,
      waiting_label_ar: str(raw["waiting_label_ar"]),
      will_escalate: raw["will_be_assigned"] === true,
      manual_entry: MANUAL_SOURCES.has(task.source),
    };
  });
}

export async function fetchOverdue(dept: string | null): Promise<TeamTask[]> {
  const path = "/tasks/overdue";
  const res = await api.get<unknown>(path, { query: { scope: "department", dept } });
  return asArray(res.data, path).map((raw) => normalizeTask(raw, path));
}

/** أعضاء الفريق بلا حِمل — طلب واحد خفيف (بيستخدمه كمان اختيار وجهة التوزيع) */
export async function fetchMembers(dept: string | null): Promise<TeamMember[]> {
  const path = "/admin/users";
  const res = await api.get<unknown>(path, { query: { department: dept, per_page: 100 } });
  return asArray(res.data, path).map((raw) => {
    const id = str(raw["id"]);
    if (!id) fail(path, "عضو بلا معرّف في الرد");
    return {
      id,
      name_ar: str(raw["display_name_ar"]) || str(raw["full_name_ar"]) || "بلا اسم",
      role_ar: strOrNull(raw["role_title_ar"]),
      can_receive: str(raw["status"]) === "active",
      open_tasks: 0,
      overdue_tasks: 0,
      closed_today: null,
      last_activity_at: null,
    };
  });
}

/**
 * أعضاء الفريق + حِملهم.
 * الحِمل بيتجمّع من مهام القسم المفتوحة (`open` + `claimed`).
 */
export async function fetchWorkload(dept: string | null): Promise<Workload> {
  const members = await fetchMembers(dept);
  const byId = new Map(members.map((m) => [m.id, m]));
  const tasksPath = "/tasks";
  let page = 1;
  let scanned = 0;
  let partial = false;
  const strangers = new Set<string>();

  for (;;) {
    const res = await api.get<unknown>(tasksPath, {
      query: {
        scope: "department",
        dept,
        state: "open,claimed",
        sort: "created_at",
        page,
        per_page: WORKLOAD_PAGE_SIZE,
      },
    });
    const rows = asArray(res.data, tasksPath);
    for (const raw of rows) {
      const task = normalizeTask(raw, tasksPath);
      scanned += 1;
      if (!task.assignee_id) continue; // لسه في الطابور — بيتعرض فوق مش كحِمل
      const member = byId.get(task.assignee_id);
      if (!member) {
        strangers.add(task.assignee_id);
        continue;
      }
      member.open_tasks += 1;
      if (task.is_overdue) member.overdue_tasks += 1;
    }
    if (res.meta?.has_more !== true) break;
    page += 1;
    if (page > MAX_WORKLOAD_PAGES) {
      partial = true;
      break;
    }
  }

  if (strangers.size > 0) {
    // ⛔ ممنوع الفشل الصامت: مهام على حسابات مش ظاهرة في نطاقنا
    console.error(
      `[team] فيه مهام على ${strangers.size} حساب مش ظاهر في قايمة الفريق — الحِمل المعروض ناقص`,
    );
  }

  members.sort((a, b) => b.open_tasks - a.open_tasks || a.name_ar.localeCompare(b.name_ar, "ar"));
  return { members, partial, scanned };
}

/** بيجيب أقسام الشاشة التلاتة — كل قسم بيفشل لوحده */
export async function loadTeamBoard(dept: string | null): Promise<TeamBoard> {
  const [queue, overdue, workload] = await Promise.all([
    fetchQueue(dept).then(
      (value) => ({ ok: true, value }) as Loaded<QueueItem[]>,
      (err: unknown) => toFailure(err, "الطابور"),
    ),
    fetchOverdue(dept).then(
      (value) => ({ ok: true, value }) as Loaded<TeamTask[]>,
      (err: unknown) => toFailure(err, "المتأخرات"),
    ),
    fetchWorkload(dept).then(
      (value) => ({ ok: true, value }) as Loaded<Workload>,
      (err: unknown) => toFailure(err, "الحِمل"),
    ),
  ]);
  return { queue, overdue, workload };
}

/* ══════════════ الكتابة ══════════════ */

/** استلام مهمة من الطابور — ذرّي في السيستم: اتنين بيدوسوا ⇒ واحد بس ينجح */
export async function claimTask(taskId: string): Promise<TeamTask> {
  const path = `/tasks/${taskId}/claim`;
  const res = await api.post<unknown>(path, undefined, {
    idempotencyKey: newIdempotencyKey(),
  });
  if (!res.data || typeof res.data !== "object") fail(path, "الاستلام رجّع رد فاضي");
  return normalizeTask(res.data as Record<string, unknown>, path);
}

/**
 * إعادة توزيع — الوجهة إجبارية هنا عشان الشاشة تكون قالتها للمستخدم قبل
 * الضغط، و`version` بيمشي كقفل تفاؤلي: لو حد حرّك المهمة وإنت فاتح
 * الشاشة، السيستم بيرفض بدل ما يدوس على شغله.
 */
export async function reassignTask(
  taskId: string,
  toUserId: string,
  reasonAr: string,
  version: number,
): Promise<TeamTask> {
  const path = `/tasks/${taskId}/reassign`;
  const res = await api.post<unknown>(
    path,
    { to_user_id: toUserId, reason_ar: reasonAr },
    { ifMatch: version },
  );
  if (!res.data || typeof res.data !== "object") fail(path, "إعادة التوزيع رجّعت رد فاضي");
  return normalizeTask(res.data as Record<string, unknown>, path);
}

/* ══════════════ قواعد التوزيع ══════════════ */

/**
 * 🔴 بلاغان اتصلحوا هنا على مرحلتين:
 *
 *  ١. الزرار كان بيوعد «بتنقل لعضو أقل حِملًا» وهو واقف على صف صاحبه **هو**
 *     الأقل حِملًا — الوعد كذب. الحل وقتها: الوجهة اتكتبت على الزرار.
 *  ٢. لكن كده الفعل كان **بيتعطّل تمامًا** لو مفيش حد أقل حِملًا — والمدير
 *     عنده أسباب تانية للنقل غير الحِمل (إجازة · لغة · خبرة بالأسرة).
 *     دلوقتي **المدير بيختار الشخص**، والدالة دي بقت **اقتراح** بس:
 *     بترجّع أخف واحد عشان يبقى هو المختار افتراضيًا في القايمة.
 *
 * ⛔ اللي فضل مقفول: مفيش وجهة مخفية. أي نقل لازم يكون الشخص معروض
 *    بالاسم وبحِمله قبل الضغطة.
 */
export function suggestReassignTarget(
  members: TeamMember[],
  currentAssigneeId: string | null,
): TeamMember | null {
  const candidates = reassignCandidates(members, currentAssigneeId);
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (b.open_tasks < a.open_tasks ? b : a));
}

/** كل اللي ينفع ينقلوا ليهم: نشط، ومش صاحب المهمة دلوقتي. */
export function reassignCandidates(
  members: TeamMember[],
  currentAssigneeId: string | null,
): TeamMember[] {
  return members.filter((m) => m.can_receive && m.id !== currentAssigneeId);
}
